/** Media Preview Modal / Lightbox — shared by every image/video Media Card.

 * Image quality contract:
 * - `src` = DISPLAY_PREVIEW (immediate)
 * - `masterSrc` = GENERATED_MASTER / SOURCE_ORIGINAL (progressive upgrade)
 * Never CSS-scale a preview and label it as hi-res.
 *
 * Images use CanvasHiResImageViewer (wheel/pan/dblclick inspection).
 * Video keeps the compact modal player with playback ownership handoff.
 */

import { Modal } from 'antd'
import { useEffect, useRef, type ReactNode } from 'react'
import CanvasHiResImageViewer, { type CanvasHiResMasterMeta } from './CanvasHiResImageViewer'
import { claimPlayback, releasePlayback } from './videoPlaybackCoordinator'

export type { CanvasHiResMasterMeta }

interface Props {
  open: boolean
  onClose: () => void
  kind: 'image' | 'video'
  /** Immediate display source (preview / video). */
  src: string | null
  /** Optional master/original for progressive hi-res swap (images only). */
  masterSrc?: string | null
  /** Stable asset identity — required for video ownership handoff. */
  assetId?: string | null
  /** Canvas currentTime to resume from (seconds). */
  startTime?: number
  /** Structured master metadata (preferred). */
  masterMeta?: CanvasHiResMasterMeta | null
  /** Optional master metadata chip override (legacy). */
  masterMetaLabel?: string | null
  /** Optional bottom-right business actions preserved across zoom/pan. */
  footerActions?: ReactNode
}

function applyStartAndPlay(video: HTMLVideoElement, startTime: number) {
  const run = () => {
    if (startTime > 0.05 && Number.isFinite(startTime)) {
      try {
        video.currentTime = startTime
      } catch {
        /* ignore seek errors before metadata */
      }
    }
    void video.play().catch(() => {
      /* autoplay may be blocked; native controls remain */
    })
  }
  if (video.readyState >= 1) run()
  else video.addEventListener('loadedmetadata', run, { once: true })
}

export default function MediaLightbox({
  open,
  onClose,
  kind,
  src,
  masterSrc = null,
  assetId,
  startTime = 0,
  masterMeta = null,
  masterMetaLabel = null,
  footerActions = null,
}: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const tokenRef = useRef<symbol | null>(null)
  const identity = assetId || src || ''
  const startTimeRef = useRef(startTime)
  startTimeRef.current = startTime

  useEffect(() => {
    if (!open || kind !== 'video' || !src || !identity) {
      releasePlayback(identity, tokenRef.current)
      tokenRef.current = null
      return
    }
    tokenRef.current = claimPlayback(identity, 'MODAL', startTime || 0)
    return () => {
      releasePlayback(identity, tokenRef.current)
      tokenRef.current = null
    }
  }, [open, kind, src, identity, startTime])

  const setVideoEl = (el: HTMLVideoElement | null) => {
    videoRef.current = el
    if (el && open && kind === 'video') {
      applyStartAndPlay(el, startTimeRef.current)
    }
  }

  if (kind === 'image') {
    return (
      <CanvasHiResImageViewer
        open={open && Boolean(src)}
        onClose={onClose}
        displaySrc={src}
        masterSrc={masterSrc}
        masterMeta={masterMeta}
        masterMetaLabel={masterMetaLabel}
        footerActions={footerActions}
      />
    )
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width="min(92vw, 1280px)"
      className="pcv-lightbox-modal"
      destroyOnClose
      afterOpenChange={(isOpen) => {
        if (isOpen && kind === 'video' && videoRef.current) {
          applyStartAndPlay(videoRef.current, startTimeRef.current)
        }
      }}
    >
      {src ? (
        <video ref={setVideoEl} className="pcv-lightbox-media" src={src} controls playsInline />
      ) : null}
    </Modal>
  )
}
